import { Component, OnInit, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ApprovalService } from '../../../services/approval.service';
import { ApprovalRequest } from '../../../models/approval.model';
import { DataTableComponent, TableColumn } from '../../../components/shared/data-table/data-table.component';
import { AdminModalComponent } from '../../../components/shared/admin-modal/admin-modal.component';
import { StatusBadgeComponent } from '../../../components/shared/status-badge/status-badge.component';
import { FormsModule } from '@angular/forms';
import { take } from 'rxjs';

@Component({
  selector: 'app-approval-management',
  standalone: true,
  imports: [
    CommonModule, 
    DataTableComponent, 
    AdminModalComponent,
    StatusBadgeComponent,
    FormsModule
  ],
  templateUrl: './approval-management.component.html',
  styleUrls: ['./approval-management.component.scss']
})
export class ApprovalManagementComponent implements OnInit {
  private approvalService = inject(ApprovalService);

  requests = signal<ApprovalRequest[]>([]);
  loading = signal(false);
  isModalOpen = signal(false);
  isSubmitting = signal(false);
  
  selectedRequest: ApprovalRequest | null = null;
  remarks: string = '';

  columns: TableColumn[] = [
    { key: 'targetCollection', label: 'Module', sortable: true },
    { key: 'operation', label: 'Action', type: 'badge' },
    { key: 'createdAt', label: 'Requested On', type: 'date' },
    { key: 'status', label: 'Status', type: 'badge' },
    { key: 'actions', label: 'Actions', type: 'actions' }
  ];

  pendingCount = computed(() => this.requests().filter(r => r.status === 'PENDING').length);
  approvedToday = computed(() => this.requests().filter(r => r.status === 'APPROVED').length); // Simplified for now

  ngOnInit(): void {
    this.loadData();
  }

  loadData() {
    this.loading.set(true);
    this.approvalService.getPendingRequests().pipe(take(1)).subscribe({
      next: (reqs) => {
        this.requests.set(reqs);
        this.loading.set(false);
      },
      error: (error) => {
        console.error('Error loading requests:', error);
        this.loading.set(false);
      }
    });
  }

  openReviewModal(request: ApprovalRequest) {
    this.selectedRequest = request;
    this.remarks = '';
    this.isModalOpen.set(true);
  }

  async handleTableAction(event: { type: string, item: any }) {
    if (event.type === 'edit') {
      this.openReviewModal(event.item);
    }
  }

  async approve() {
    if (!this.selectedRequest) return;
    this.isSubmitting.set(true);
    try {
      await this.approvalService.approveRequest(this.selectedRequest.id!, this.remarks);
      this.isModalOpen.set(false);
      this.loadData();
    } catch (error) {
      console.error('Error approving:', error);
    } finally {
      this.isSubmitting.set(false);
    }
  }

  async reject() {
    if (!this.selectedRequest || !this.remarks) {
      alert('Remarks are required for rejection.');
      return;
    }
    this.isSubmitting.set(true);
    try {
      await this.approvalService.rejectRequest(this.selectedRequest.id!, this.remarks);
      this.isModalOpen.set(false);
      this.loadData();
    } catch (error) {
      console.error('Error rejecting:', error);
    } finally {
      this.isSubmitting.set(false);
    }
  }
}
